import React, { useState, useEffect } from "react";
import useServiceStore from "../../../store/builders/useServiceStore";
  
const ServicesTile = () => {
  const { 
    fetchServices, fetchBuilderService, allServices, builderServices, 
    fetchCreateService, deleteService, addServiceToBuilder, isLoading 
  } = useServiceStore();
  
  // Add these new states
  const SERVICE_LIMIT = 10;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [isLimitPopupOpen, setIsLimitPopupOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [newServiceName, setNewServiceName] = useState("");

  useEffect(() => {
    fetchServices();
    fetchBuilderService();
  }, [fetchServices, fetchBuilderService]);

  const addServiceToTable = async () => {
    if (selectedService) {
      try {
        // Check if service limit is reached
        if (builderServices.length >= SERVICE_LIMIT) {
          setIsLimitPopupOpen(true);
          setIsPopupOpen(false);
          return;
        }

        const serviceToAdd = allServices.find(service => service.name === selectedService);
        if (serviceToAdd) {
          await addServiceToBuilder(serviceToAdd.id);
          await fetchBuilderService();
          setSelectedService("");
          setIsPopupOpen(false);
        }
      } catch (error) {
        console.error("Failed to add service", error);
      }
    }
  };

  const createNewService = async () => {
    if (newServiceName.trim()) {
      try {
        await fetchCreateService({ name: newServiceName.trim() });
        await fetchServices(); // Fetch updated services list
        await fetchBuilderService();
        setNewServiceName("");
        setIsCreatePopupOpen(false);
      } catch (error) {
        console.error("Failed to create service", error);
      }
    }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      await deleteService(serviceId);
      await fetchServices(); // Fetch updated services list
      await fetchBuilderService();
    } catch (error) {
      console.error("Failed to delete service", error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center px-3 py-2">
      <div className="w-full max-w-3xl bg-white p-3 rounded-md shadow-sm mb-4">
        <div className="flex gap-2">
          <button
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
            onClick={() => setIsPopupOpen(true)}
          >
            Add
          </button>
          <button
            className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
            onClick={() => setIsCreatePopupOpen(true)}
          >
            Create
          </button>
        </div>
      </div>

      <div className="w-full max-w-3xl bg-white rounded-md shadow-sm overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-center px-2 py-1 text-xs font-medium text-gray-700">Services</th>
              <th className="text-center px-2 py-1 text-xs font-medium text-gray-700">Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="2" className="text-center py-2 text-sm text-gray-600">Loading...</td>
              </tr>
            ) : (
              builderServices.map((srv, index) => (
                <tr key={srv.id} className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}>
                  <td className="text-center px-2 py-1 text-xs text-gray-700">{srv.name}</td>
                  <td className="text-center px-2 py-1">
                    <button
                      className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                      onClick={() => handleDeleteService(srv.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center z-50" style={{ paddingTop: '25vh' }}>
          <div className="bg-white rounded-md p-4 shadow-md w-64 h-fit">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-medium text-gray-800">Add Services</h2>
              <button className="text-gray-600 hover:text-gray-800" onClick={() => setIsPopupOpen(false)}>×</button>
            </div>
            <div className="mb-3 relative">
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
                style={{ position: 'relative' }}
              >
                <option value="">Select a Service</option>
                {isLoading ? (
                  <option>Loading...</option>
                ) : (
                  allServices.map((srv, index) => (
                    <option key={index} value={srv.name}>{srv.name}</option>
                  ))
                )}
              </select>
            </div>
            <button
              className="w-full px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              onClick={addServiceToTable}
            >
              Add Service
            </button>
          </div>
        </div>
      )}

      {isCreatePopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center z-50" style={{paddingTop: '25vh'}}>
          <div className="bg-white rounded-md p-4 shadow-md w-64 h-fit">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-medium text-gray-800">Create Service</h2>
              <button className="text-gray-600 hover:text-gray-800" onClick={() => setIsCreatePopupOpen(false)}>×</button>
            </div>
            <div className="mb-3">
              <input
                type="text"
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="Enter Service Name"
                className="w-full px-3 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <button
              className="w-full px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              onClick={createNewService}
            >
              Create Service
            </button>
          </div>
        </div>
      )}
      {/* Add this new popup for limit message */}
      {isLimitPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-md p-4 shadow-md w-80">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-sm font-medium text-gray-800">Service Limit Reached</h2>
              <button className="text-gray-600 hover:text-gray-800" onClick={() => setIsLimitPopupOpen(false)}>×</button>
            </div>
            <div className="mb-4 text-sm text-gray-600">
              <p>You have reached the maximum limit of {SERVICE_LIMIT} services.</p>
              <p className="mt-2">To add a new service, please remove an existing service first.</p>
            </div>
            <button
              className="w-full px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              onClick={() => setIsLimitPopupOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesTile;
